/**
 * @file InteractionObserver.ts
 * This file contains the InteractionObserver implementation, which is used to
 * trigger events from InteractionEventsManager based on the current mouse position
 * which we'll cross check with the object registry.
 *
 * We need to know when a click is happened and when the hover event is triggered
 */

import type * as PIXI from "pixi.js";
import { getDistance } from "../../utils/geometry";
import type { Box } from "../coreComponents/Box";
import {
  getElementByKey,
  getRootNode,
  isComponentMounted
} from "../renderingEngine/renderer";
import {
  getNodesAtPoint,
  moveNodeToDragLayer,
  moveNodeToMainLayer
} from "./InteractionObserver.utils";
import { dragContext, dragContextStore } from "./dragContext";
import type { DragType } from "./interactionObserverConstants";
import {
  ClickType,
  InteractionObserverEvent,
  PointerEvents
} from "./interactionObserverConstants";
import { triggerEvent } from "./interactionTrigger";
import type { CustomPointerEvent } from "./interactionTypes";
import { appState } from "../../utils/store";
import { renderFlags } from "../layoutEngine/renderFlags";
import type { CoreComponent } from "../coreComponents/coreComponents.types";
import {
  disableViewportDrag,
  enableViewportDrag
} from "../../pixi-components/layers/viewport/viewport.layer";
import { devLogger } from "@certa/common/utils/devLogger.ts";

let interactionLayer: PIXI.Container | null = null; // This is the container where all the events will be registered

// This is maximum distance between mouse down and mouse up to be considered as a click
const MAX_CLICK_DISTANCE = 5; // in pixels
// Stores history of events. Doesn't contain multiple events of the same type consecutively.
type EventHistoryItem = Pick<
  CustomPointerEvent,
  "x" | "y" | "type" | "clickType"
> & {
  nodesAtPoint: CoreComponent[];
};

/**
 * eventHistory stores the history of events. It doesn't contain multiple events of the same type
 * consecutively. It only contains upto 3 events.
 *
 * In case of pointermove, it only stores the last event and replaces the previous one.
 */
const eventHistory: EventHistoryItem[] = [];

/**
 * This function just saves the given event in the eventHistory array.
 * In case of pointermove being the last event recorded, it just replaces it.
 * But only when it is the last one. If it is not the last one, it just pushes
 * the new one into the history like with every other event.
 * @param event EventHistoryItem
 * @returns void
 **/
const saveEvent = (event: EventHistoryItem) => {
  const lastEvent = eventHistory.length
    ? eventHistory[eventHistory.length - 1]
    : null;

  if (
    lastEvent !== null &&
    lastEvent.type === InteractionObserverEvent.POINTERMOVE
  ) {
    eventHistory.splice(eventHistory.length - 1, 1, event);
  } else {
    eventHistory.push(event);
    if (eventHistory.length > 3) {
      eventHistory.splice(0, 1);
    }
  }
};

/**
 * This function simplifies the event object and returns a new object
 * with only the required data, which is x, y and type.
 * x, y are according to the local coordinates of the interactionLayer, whereas
 * event.data.global.x/y are according to the screen's coordinates.
 *
 * Therefore the final x, y are more usable values, as things like zooming,
 * and panning are already taken into account.
 * @param event PIXI.InteractionEvent
 * @returns EventHistoryItem
 * */
const simplifyEvent = (
  event: PIXI.FederatedMouseEvent
): EventHistoryItem | null => {
  if (!interactionLayer) return null;

  const clickType: ClickType = event.buttons;
  const { type } = event;
  // convert to local coordinates to PIXI
  const { x, y } = interactionLayer.toLocal(event.data.global);

  return {
    x,
    y,
    type,
    clickType,
    nodesAtPoint: getNodesAtPoint({ x, y })
  };
};

/**
 * This function handles the pointer events triggered from PIXI and
 * triggers the appropriate events from InteractionEventsManager.
 * This function currently handles the following
 * - When a node is clicked
 * - When is node is hovered over
 * - When a node is hovered out
 * - When a mouse is moved over a node
 */
const handlePointerAction = (_event: PIXI.FederatedPointerEvent) => {
  // FUN FACT: this _event is actually a reference which changes during subsequent calls
  // so if you try to save this exact event as it is in the history, it will change
  // therefore we need to save the event as a new object, with selective data as well

  // srcElement is the element on which the event was triggered from. In case of html elements it will return the reference to the element
  const srcElement = _event.nativeEvent.target;

  // If the srcElement is a canvas, then we will trigger the events as the intent is correct
  if (srcElement instanceof HTMLCanvasElement) {
    const event = simplifyEvent(_event);
    // set the cursor position
    appState.localCursorPosition = { x: event?.x ?? 0, y: event?.y ?? 0 };

    commonPointerHandler(event);
  } else {
    appState.localCursorPosition = { x: -Infinity, y: -Infinity };
  }
};

const commonPointerHandler = (event: ReturnType<typeof simplifyEvent>) => {
  // event will be null if an event was fired but the interaction layer was not initialized
  // so we will just ignore those events.
  if (!event) return;

  // check if current event is already in the history
  const lastEvent = eventHistory.length
    ? eventHistory[eventHistory.length - 1]
    : null;
  // second last event
  const secondLastEvent =
    eventHistory.length > 1 ? eventHistory[eventHistory.length - 2] : null;

  saveEvent(event);
  if (lastEvent === null) {
    return;
  }

  const flags = {
    updateDrag: false, // same flag will also be used to check if drop event needs to be triggered
    checkDragStart: false,
    checkDragEnd: false,
    clicked: false,
    dropped: false
  };

  const nodesPointerOut: CoreComponent[] = [];
  const nodesPointerMove: CoreComponent[] = [];
  const nodesPointerOver: CoreComponent[] = [];

  const { nodesAtPoint: nodesCurrentlyHovered } = event;

  const isDragging = dragContext.node !== null; // condition that determines if a drag is in-progress
  // Get the current event type
  const { type, clickType } = event;
  if (type === InteractionObserverEvent.POINTERMOVE) {
    if (isDragging && clickType !== ClickType.LEFT_CLICK) {
      flags.checkDragEnd = true;
    } else if (lastEvent.type === InteractionObserverEvent.POINTERMOVE) {
      // If the last event was a pointermove, then we need to check if
      // this is drag or a hover in/out event
      if (
        secondLastEvent &&
        secondLastEvent.type === InteractionObserverEvent.POINTERDOWN &&
        secondLastEvent.clickType === ClickType.LEFT_CLICK
      ) {
        // This is a drag event

        if (isDragging) {
          // enable flag for updating dragged element's parameters
          flags.updateDrag = true;
        } else {
          // If we're not dragging anything, then we trigger "DragStart" event
          // on the node that is being dragged
          flags.checkDragStart = true;
        }
      } else {
        if (isDragging) {
          // enable flag for updating dragged element's parameters
          flags.checkDragEnd = true;
        }
        // extract the nodes that were previously hovered
        const { nodesAtPoint: nodesPreviouslyHovered } = lastEvent;

        // find Nodes that just got hovered
        nodesCurrentlyHovered.forEach(node => {
          if (!nodesPreviouslyHovered.includes(node)) {
            nodesPointerOver.push(node);
          }
        });

        // separate nodes that are still hovered and nodes that are not,
        // between the previous event and the current event
        nodesPreviouslyHovered.forEach(node => {
          if (nodesCurrentlyHovered.includes(node)) {
            nodesPointerMove.push(node);
          } else if (isComponentMounted(node.props.key)) {
            // We need to check if the node is still mounted,
            // because it's possible that the node was unmounted
            // in which case you cannot fire events on it.
            nodesPointerOut.push(node);
          }
        });
      }
    }
  } else if (type === InteractionObserverEvent.POINTERUP) {
    // If the last event was a pointerdown, then we need to check if
    // this is a click or a drag end event
    const lastPointerDownEvent =
      (lastEvent.type === InteractionObserverEvent.POINTERDOWN &&
        lastEvent.clickType === ClickType.LEFT_CLICK) ||
      (lastEvent.type === InteractionObserverEvent.POINTERDOWN &&
        lastEvent.clickType === ClickType.NONE)
        ? lastEvent
        : (secondLastEvent?.type === InteractionObserverEvent.POINTERDOWN &&
              secondLastEvent.clickType === ClickType.LEFT_CLICK) ||
            (secondLastEvent?.type === InteractionObserverEvent.POINTERDOWN &&
              secondLastEvent.clickType === ClickType.NONE)
          ? secondLastEvent
          : null;

    if (lastPointerDownEvent) {
      const distance = getDistance(lastPointerDownEvent, event);

      if (distance <= MAX_CLICK_DISTANCE) {
        // This is a click event
        flags.clicked = true;
      } else {
        // Drop event
        if (isDragging) {
          // eslint-disable-next-line no-console
          console.log("Dropped / Drag End");
          // Also check if we're hovering over potential drop targets
          flags.dropped = true;
        }
      }
    } else {
      // TODO: Could be dragged in from outside of interaction layer situation
      return;
    }
  }

  /**
   * Now that we've determined all the flags, we can start triggering events
   * Priority of events is as follows:
   * 1. DroppingOut (when the dragged element is being dragged out of a drop target)
   * 2. Drag update (when something is already being dragged)
   * 3. Dropping (when something is being dragged and pointer is up)
   * 4. Drag start (when something wasn't being dragged but just started dragging)
   * 5. Hover out (when something was hovered over, but now pointer is not over it anymore)
   * 6. Hover over (when something is just hovered over)
   * 7. Hover move (when something is already hovered over and pointer just moves over it)
   * 8. Click (when something is clicked)
   * 9. Dropped + Drag end (when something is dropped)
   */

  // Preparing drop targets
  const dropTargets = [];
  if (dragContext.node) {
    for (const node of nodesCurrentlyHovered) {
      // TODO: We are currently always assuming that node is a
      // Box. We need to make this in a way that we don't need to
      // typecast it.
      // https://thevetted.atlassian.net/browse/DS-4772
      const boxComponent = node as Box;
      if (
        boxComponent &&
        boxComponent.props.dropTypes &&
        boxComponent.props.dropTypes.length > 0
      ) {
        //
        const dropTypes = boxComponent.props.dropTypes;
        const dragType = dragContext.node.props.dragType;
        if (
          dragType &&
          dropTypes !== undefined &&
          node !== dragContext.node && // Can't drop on itself
          // TODO: Since "step-xyz" is getting dragged, step-xyz-header should not be
          // considered as a drop target
          !node.props.key.includes(dragContext.node.props.key) &&
          dropTypes.includes(dragType)
        ) {
          dropTargets.push(node);
        }
      }
    }
  }

  /**
   * 1. Dropping out scenario
   */

  if (dragContext.node) {
    const { nodesAtPoint: nodesPreviouslyHovered } = lastEvent;
    const eventPayload = {
      ...event,
      type: PointerEvents.DROPPING_OUT,
      dragContextStore
    };
    for (const node of nodesPreviouslyHovered) {
      if (!isComponentMounted(node.props.key)) {
        // We need to check if the node is still mounted,
        // because it's possible that the node was unmounted
        // in which case you cannot fire events on it.
        continue;
      }
      if (!nodesCurrentlyHovered.includes(node)) {
        const triggerResult = triggerEvent(
          node.props.key,
          PointerEvents.DROPPING_OUT,
          eventPayload
        );
        if (triggerResult.handled) {
          break;
        }
      }
    }
  }

  // 2. Check for Drag update
  if (flags.updateDrag && dragContext.node) {
    // trigger drag event
    triggerEvent(dragContext.node.props.key, PointerEvents.DRAG, {
      ...event,
      type: PointerEvents.DRAG
    });

    // update the dragged node's position
    dragContext.position.x = event.x;
    dragContext.position.y = event.y;

    renderFlags.layoutDirty = true;
    // // update the dragged node's offsets
    // dragContext.offset.x = event.x - dragContext.node.x;
    // dragContext.offset.y = event.y - dragContext.node.y;

    // 3. Check for Dropping
    if (dropTargets.length > 0) {
      // TODO check which nodes allow dropping
      const eventPayload = {
        ...event,
        type: PointerEvents.DROPPING,
        dragContextStore
      };
      for (const node of dropTargets) {
        const triggerResult = triggerEvent(
          node.props.key,
          PointerEvents.DROPPING,
          eventPayload
        );
        if (triggerResult.handled) {
          // We break loop because we don't want to trigger dropping event on
          // multiple nodes
          break;
        }
      }
    }
    // return;
  }

  // 4. Check for Drag start
  if (flags.checkDragStart && dragContext.node === null && !flags.updateDrag) {
    for (const node of nodesCurrentlyHovered) {
      // Drag start is checked when pointer is clicked and moved. However, we also need
      // to ensure that click event and move events were fired on the same node,
      // before initiating the drag-start event. Because it's possible that user clicked
      // outside the node and moved the pointer over the node (while holding the click).
      // In that case, we do not want to trigger drag-start event.
      if (node && secondLastEvent?.nodesAtPoint.includes(node)) {
        // TODO: We are currently always assuming that node is a
        // Box. We need to make this in a way that we don't need to
        // typecast it.
        // https://thevetted.atlassian.net/browse/DS-4772
        const boxComponent = node as Box;
        if (boxComponent.props.dragType) {
          const draggedElementKey = boxComponent.props.dragHandleFor || "";
          const draggedNode = getElementByKey(draggedElementKey) as Box;
          if (!draggedNode) continue;

          dragContext.node = draggedNode;
          dragContext.position.x = event.x;
          dragContext.position.y = event.y;
          dragContext.offset.x = event.x - draggedNode.getLayoutValues().x;
          dragContext.offset.y = event.y - draggedNode.getLayoutValues().y;

          moveNodeToDragLayer({
            node: dragContext.node,
            x:
              draggedNode.getLayoutValues().x -
              draggedNode.getLayoutValues().left,
            y:
              draggedNode.getLayoutValues().y -
              draggedNode.getLayoutValues().top
          });

          renderFlags.layoutDirty = true;
          triggerEvent(node.props.key, PointerEvents.DRAGSTART, {
            ...event,
            type: PointerEvents.DRAGSTART,
            dragContextStore
          });
          disableViewportDrag();

          // unregister draggedNode from spatialRegistry
          // spatialRegistry.removeTree(draggedNode);

          break;
        }
      }
    }
  }

  // 5. Check for Hover out
  nodesPointerOut.forEach(node => {
    triggerEvent(node.props.key, PointerEvents.POINTEROUT, {
      ...event,
      type: PointerEvents.POINTEROUT
    });
  });

  // 6. Check for Hover over
  nodesPointerOver.forEach(node => {
    triggerEvent(node.props.key, PointerEvents.POINTEROVER, {
      ...event,
      type: PointerEvents.POINTEROVER
    });
  });

  // 7. Check for Hover move
  nodesPointerMove.forEach(node => {
    triggerEvent(node.props.key, PointerEvents.POINTERMOVE, {
      ...event,
      type: PointerEvents.POINTERMOVE
    });
  });

  // 8. Check for Click
  if (flags.clicked) {
    // If no nodes are clicked, it means that the click was out of bounds of the
    // nodes. In that case, we trigger the click event on the root node.
    if (nodesCurrentlyHovered.length === 0) {
      // Emit the even on the root node
      const rootNode = getRootNode();
      if (rootNode) {
        triggerEvent(rootNode.key, PointerEvents.CLICK, {
          ...event,
          type: PointerEvents.CLICK
        });
      }

      return;
    }

    // If there are nodes under the mouse, then we trigger the click event
    for (const node of nodesCurrentlyHovered) {
      const triggerResult = triggerEvent(node.props.key, PointerEvents.CLICK, {
        ...event,
        type: PointerEvents.CLICK
      });
      if (triggerResult.propagationStopped || triggerResult.handled) {
        break;
      }
    }
  }

  // 9. Check for Dropped + Drag end
  if (flags.dropped && dragContext.node) {
    enableViewportDrag();
    if (dropTargets.length > 0) {
      for (const node of dropTargets) {
        devLogger.log("Dropped on", node.props.key);
        const triggerResult = triggerEvent(
          node.props.key,
          PointerEvents.DROPPED,
          {
            ...event,
            type: PointerEvents.DROPPED,
            dragContextStore
          }
        );
        if (triggerResult.handled) {
          // We break loop because we don't want to trigger dropping event on
          // multiple nodes
          break;
        }
      }
    }

    triggerEvent(dragContext.node.props.key, PointerEvents.DRAGEND, {
      ...event,
      type: PointerEvents.DRAGEND
    });

    moveNodeToMainLayer(dragContext.node);

    // spatialRegistry.insertTree(dragContext.node);

    dragContext.node = null;
    dragContext.position.x = 0;
    dragContext.position.y = 0;
    dragContext.offset.x = 0;
    dragContext.offset.y = 0;

    renderFlags.layoutDirty = true;
    // This is to remove any changes that may have been done in DOM
    // while the drag was active -- such as drop indicators.
    renderFlags.domDirty = true;
    return;
  }

  /**
   * A case where drag stays active but not cancelled. This will
   * help in cancelling it.
   */
  if (flags.checkDragEnd && dragContext.node) {
    enableViewportDrag();
    triggerEvent(dragContext.node.props.key, PointerEvents.DRAGEND, {
      ...event,
      type: PointerEvents.DRAGEND
    });

    moveNodeToMainLayer(dragContext.node);

    // spatialRegistry.insertTree(dragContext.node);

    dragContext.node = null;
    dragContext.position.x = 0;
    dragContext.position.y = 0;
    dragContext.offset.x = 0;
    dragContext.offset.y = 0;

    renderFlags.layoutDirty = true;
    // This is to remove any changes that may have been done in DOM
    // while the drag was active -- such as drop indicators.
    renderFlags.domDirty = true;
    return;
  }
};

/**
 * Initialize the interaction observer.
 * This will add the event listeners to the PIXI renderer.
 *
 * All the events are registered on the provided container, which we treat
 * as the interaction layer
 */
export const initInteractionObserver = (container: PIXI.Container) => {
  if (interactionLayer !== null) {
    const error =
      "Interaction layer already initialized. Only one interaction layer can be initialized at a time.";
    throw Error(error);
  }
  interactionLayer = container;
  container.addListener(
    InteractionObserverEvent.POINTERDOWN,
    handlePointerAction
  );
  container.addListener(
    InteractionObserverEvent.POINTERUP,
    handlePointerAction
  );
  container.addListener(
    InteractionObserverEvent.POINTERMOVE,
    handlePointerAction
  );

  // Return a function to remove the event listeners
  return () => {
    interactionLayer = null;
    container.removeListener(
      InteractionObserverEvent.POINTERDOWN,
      handlePointerAction
    );
    container.removeListener(
      InteractionObserverEvent.POINTERUP,
      handlePointerAction
    );
    container.removeListener(
      InteractionObserverEvent.POINTERMOVE,
      handlePointerAction
    );
  };
};

/**
 * Trigger a synthetic pointer move event at the given coordinates.
 * This is useful when we need to trigger a pointer move event
 * programmatically, during auto-pan event as actual pointer may not be
 * getting moved by the user, however since viewport is moving, we need
 * to trigger the pointer move event to fire this event so that everything
 * dependent on it is able to update their respective positions.
 * @param x
 * @returns
 */
export const triggerSyntheticPointerEvent = ({
  globalX,
  globalY,
  clickType,
  type
}: {
  globalX: number; // Global / Screen X coordinate
  globalY: number; // Global / Screen Y coordinate
  clickType: ClickType;
  type: InteractionObserverEvent;
}) => {
  if (!interactionLayer) return;
  const { x, y } = interactionLayer.toLocal({ x: globalX, y: globalY });
  commonPointerHandler({
    x,
    y,
    type,
    clickType,
    nodesAtPoint: getNodesAtPoint({ x, y })
  });
};
/**
 * Accepts the global point and returns the nodes that are found
 * on that point, which also allows dropping of the given dragType.
 * @returns
 */
export const getAvailableDropTargetsAndHoveredNodes = ({
  x,
  y,
  dragType
}: {
  x: number;
  y: number;
  dragType: DragType;
}) => {
  // Return if interaction layer is not initialized
  if (!interactionLayer)
    return {
      dropTargets: [],
      nodesCurrentlyHovered: []
    };

  // transform the coordinates to local coordinates of the interaction layer
  const { x: x1, y: y1 } = interactionLayer.toLocal({ x, y });

  // Get the nodes that are on the given coordinates
  const nodesCurrentlyHovered = getNodesAtPoint({ x: x1, y: y1 });

  // Preparing drop targets
  const dropTargets: CoreComponent[] = [];
  for (const node of nodesCurrentlyHovered) {
    if (node) {
      const boxComponent = node as Box;
      const dropTypes = boxComponent.props.dropTypes;
      if (
        dragType &&
        dropTypes !== undefined &&
        node !== dragContext.node && // Can't drop on itself
        dropTypes.includes(dragType)
      ) {
        dropTargets.push(node);
      }
    }
  }
  return {
    dropTargets,
    nodesCurrentlyHovered
  };
};
